import { organizationApi, retriableApiRequest } from '@ardoq/api';
import {
  APIAdminOrganizationAttributes,
  APIPublicOrganizationAttributes,
  isAdminOrganizationAttributes,
} from '@ardoq/api-types';
import { ArdoqError, isArdoqError, returnPayload } from '@ardoq/common-helpers';
import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { filter, map, Observable, switchMap } from 'rxjs';
import { ardoqEventOperations } from 'sync/ardoqEventOperations';
import { getOrganizationEvent$ } from 'sync/wamp';
import {
  fetchCurrentOrganizationSucceeded,
  updateOrganizationSucceeded,
} from './actions';
import { currentOrganizationId$ } from './currentOrganizationId$';

export type CurrentOrganizationResult =
  | APIAdminOrganizationAttributes
  | APIPublicOrganizationAttributes
  | ArdoqError;

const getOrganizationUpdates = (orgId: string) =>
  getOrganizationEvent$(orgId).pipe(
    filter(ardoqEventOperations.isOrganizationEvent),
    filter(ardoqEventOperations.isUpdateEvent),
    map(event => event.data),
    filter(data => data._id === orgId)
  );

const organizationUpdate$ = currentOrganizationId$.pipe(
  switchMap(getOrganizationUpdates)
);

const fetchOrganizationWithRetries = (orgId: string) =>
  retriableApiRequest(() => organizationApi.fetch(orgId));

export const currentOrganization$: Observable<CurrentOrganizationResult> =
  reducedStream<CurrentOrganizationResult>(
    'organizations/currentOrganization$',
    currentOrganizationId$.pipe(switchMap(fetchOrganizationWithRetries)),
    [
      streamReducer(organizationUpdate$, returnPayload),
      reducer(fetchCurrentOrganizationSucceeded, returnPayload),
      reducer(updateOrganizationSucceeded, returnPayload),
    ]
  );

export const isLoadedAdminOrganization = (
  organization: CurrentOrganizationResult
): organization is APIAdminOrganizationAttributes => {
  if (isArdoqError(organization)) return false;

  return isAdminOrganizationAttributes(organization);
};
